import React, { useRef, useCallback } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import CardBadges from './CardBadges.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

// Module-level ref for computing drop coordinates in PlaytesterView
let hordeBattlefieldEl: HTMLElement | null = null;

export function getHordeBattlefieldEl(): HTMLElement | null {
  return hordeBattlefieldEl;
}

// ── Single horde card (non-token, rendered individually) ──────────────────────

function HordeSingleCard({ instance }: { instance: CardInstance }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `horde-${instance.instanceId}`,
    data: { instanceId: instance.instanceId, fromHordeBattlefield: true },
  });

  // Use 'large' for crisp rendering; shows full card frame (P/T box, rules text)
  const imageUri = getCardImageUri(instance.card, 'large');

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={[
        'relative rounded overflow-hidden border border-gray-600 cursor-grab',
        isDragging ? 'opacity-30' : '',
      ].join(' ')}
      style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
      title={instance.card.name}
    >
      {imageUri ? (
        <img src={imageUri} alt={instance.card.name} className="w-full h-full object-cover" style={{ borderRadius: CARD_CORNER_RADIUS }} />
      ) : (
        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
          <span className="text-gray-400 text-[8px] text-center px-1">{instance.card.name}</span>
        </div>
      )}

      {/* Counter and P/T badges */}
      <CardBadges instance={instance} />
    </div>
  );
}

// ── Token group (stacked visual with count badge) ─────────────────────────────

function HordeTokenGroup({ instances }: { instances: CardInstance[] }) {
  const representative = instances[0];
  const count = instances.length;

  // Dragging the group drags the last instance (to remove one copy)
  const dragInstance = instances[instances.length - 1];
  const dragInstanceId = dragInstance?.instanceId ?? '';
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `horde-${dragInstanceId}`,
    data: { instanceId: dragInstanceId, fromHordeBattlefield: true },
  });

  const imageUri = representative ? getCardImageUri(representative.card, 'large') : undefined;

  return (
    <div className="relative" style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}>
      {/* Stack shadow */}
      {count > 1 && (
        <div
          className="absolute rounded border border-gray-500 bg-gray-700"
          style={{ inset: 0, transform: 'translate(2px, 2px)' }}
        />
      )}

      {/* Top card */}
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={[
          'absolute inset-0 rounded overflow-hidden border border-gray-600 cursor-grab',
          isDragging ? 'opacity-30' : '',
        ].join(' ')}
        title={`${representative?.card.name ?? ''} ×${count}`}
      >
        {imageUri ? (
          <img src={imageUri} alt={representative?.card.name ?? ''} className="w-full h-full object-cover" style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
            <span className="text-gray-400 text-[8px] text-center px-1">{representative?.card.name ?? ''}</span>
          </div>
        )}
      </div>

      {/* Count badge */}
      {/* Positioned after the stacked cards, so it already paints above its own
          group. z-10 additionally lifted it above other groups, showing the count
          through cards in front once horde tokens could be placed free-form. */}
      <div className="absolute top-1 right-1 bg-black/80 text-white text-xs font-bold px-1.5 py-0.5 rounded pointer-events-none">
        ×{count}
      </div>
    </div>
  );
}

// ── HordeBattlefield ──────────────────────────────────────────────────────────

export function HordeBattlefield() {
  const battlefield = usePlaytesterStore((s) => s.horde?.battlefield ?? []);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: 'horde-battlefield' });

  // Combine droppable ref with module-level ref for drop coordinate computation
  const setNodeRef = useCallback((el: HTMLElement | null) => {
    hordeBattlefieldEl = el;
    setDropRef(el);
  }, [setDropRef]);

  // Partition: tokens (grouped by name) vs non-tokens (individual)
  const tokenGroups = new Map<string, CardInstance[]>();
  const nonTokens: CardInstance[] = [];

  for (const instance of battlefield) {
    if (instance.card.layout === 'token') {
      const key = instance.card.name;
      const group = tokenGroups.get(key) ?? [];
      group.push(instance);
      tokenGroups.set(key, group);
    } else {
      nonTokens.push(instance);
    }
  }

  return (
    <div
      ref={setNodeRef}
      className={[
        'min-h-16 flex flex-wrap gap-2 rounded p-2 transition-colors',
        isOver ? 'bg-orange-950/20 ring-1 ring-orange-500/40' : 'bg-gray-800/30',
        battlefield.length === 0 ? 'items-center justify-center' : '',
      ].join(' ')}
    >
      {battlefield.length === 0 ? (
        <span className="text-gray-600 text-xs italic">Horde Battlefield — Empty</span>
      ) : (
        <>
          {Array.from(tokenGroups.values()).map((group) => (
            <HordeTokenGroup key={group[0]?.card.name ?? ''} instances={group} />
          ))}
          {nonTokens.map((instance) => (
            <HordeSingleCard key={instance.instanceId} instance={instance} />
          ))}
        </>
      )}
    </div>
  );
}
